import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Search, Loader2, CheckCircle, Clock, QrCode, User, Mail, Phone, MapPin, IndianRupee, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyRegistrationPage = () => {
  const [searchType, setSearchType] = useState('email');
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [registration, setRegistration] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      toast.error('Please enter your email or phone number');
      return;
    }
    setSearching(true);
    setRegistration(null);
    setNotFound(false);
    try {
      const res = await axios.get(`${API}/registration-status?type=${searchType}&q=${encodeURIComponent(searchQuery.trim())}`);
      if (res.data) {
        setRegistration(res.data);
      } else {
        setNotFound(true);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setNotFound(true);
      } else {
        toast.error('Search failed. Please try again.');
      }
    } finally {
      setSearching(false);
    }
  };

  const formatAmount = (paise) => `₹${(paise / 100).toLocaleString('en-IN')}`;

  const isVerified = registration?.payment_status === 'completed';

  return (
    <div className="min-h-screen pt-32 pb-16" data-testid="my-registration-page">
      <div className="max-w-2xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="my-reg-title">
            My <span className="gradient-text">Registration</span>
          </h1>
          <p className="text-lg text-slate-300">Check your conference registration status</p>
        </div>

        {/* Search Form */}
        <div className="glass-card p-8 mb-8 neon-glow" data-testid="status-lookup">
          <h2 className="text-xl font-semibold mb-6 flex items-center gap-3">
            <Search className="h-5 w-5 text-[#FF6F4A]" />
            Find Your Registration
          </h2>

          <div className="flex gap-3 mb-4">
            <button
              type="button"
              onClick={() => { setSearchType('email'); setSearchQuery(''); setRegistration(null); setNotFound(false); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${searchType === 'email' ? 'bg-[#FF6F4A] text-white' : 'glass-card text-slate-300 hover:text-white'}`}
              data-testid="search-by-email"
            >
              Search by Email
            </button>
            <button
              type="button"
              onClick={() => { setSearchType('phone'); setSearchQuery(''); setRegistration(null); setNotFound(false); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${searchType === 'phone' ? 'bg-[#FF6F4A] text-white' : 'glass-card text-slate-300 hover:text-white'}`}
              data-testid="search-by-phone"
            >
              Search by Phone
            </button>
            <button
              type="button"
              onClick={() => { setSearchType('name'); setSearchQuery(''); setRegistration(null); setNotFound(false); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${searchType === 'name' ? 'bg-[#FF6F4A] text-white' : 'glass-card text-slate-300 hover:text-white'}`}
              data-testid="search-by-name"
            >
              Search by Name
            </button>
          </div>

          <form onSubmit={handleSearch} className="flex gap-3">
            <Input
              type={searchType === 'email' ? 'email' : 'text'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="glass-card border-white/20 text-white flex-1"
              placeholder={searchType === 'email' ? 'Enter your registered email' : searchType === 'phone' ? 'Enter your registered phone number' : 'Enter your name'}
              data-testid="status-search-input"
            />
            <Button
              type="submit"
              disabled={searching}
              className="bg-[#FF6F4A] hover:bg-[#FF6F4A]/90 text-white px-6"
              data-testid="status-search-button"
            >
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </Button>
          </form>
        </div>

        {/* Not Found */}
        {notFound && (
          <div className="glass-card p-8 text-center border-yellow-500/30" data-testid="not-found">
            <Clock className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Registration Not Found</h3>
            <p className="text-slate-400 mb-4">No registration found with this {searchType}. Please check and try again.</p>
            <a href="/registration" className="text-[#FF6F4A] hover:underline font-semibold">Register for AGOC 2026</a>
          </div>
        )}

        {/* Registration Details */}
        {registration && (
          <div className={`glass-card p-8 ${isVerified ? 'border-2 border-green-500/40' : 'border-2 border-yellow-500/40'}`} data-testid="registration-details">
            {/* Status Badge */}
            <div className="text-center mb-6">
              <div className={`inline-flex items-center gap-2 px-6 py-3 rounded-full text-lg font-bold ${
                isVerified ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
              }`} data-testid="registration-status">
                {isVerified ? <CheckCircle className="h-6 w-6" /> : <Clock className="h-6 w-6" />}
                {isVerified ? 'VERIFIED' : 'SUBMITTED - TO BE VERIFIED'}
              </div>
              {!isVerified && (
                <p className="text-sm text-slate-400 mt-3">Your registration has been received. Payment verification is pending by the AGOS committee.</p>
              )}
              {isVerified && (
                <p className="text-sm text-green-400/80 mt-3">Your registration and payment have been verified. Welcome to AGOC 2026!</p>
              )}
            </div>

            {/* Delegate Info */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3 glass-card p-4">
                <User className="h-5 w-5 text-[#FF6F4A] flex-shrink-0" />
                <div>
                  <p className="text-xs text-slate-400">Name</p>
                  <p className="text-white font-semibold" data-testid="reg-name">{registration.full_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 glass-card p-4">
                <Mail className="h-5 w-5 text-[#FF6F4A] flex-shrink-0" />
                <div>
                  <p className="text-xs text-slate-400">Email</p>
                  <p className="text-white text-sm">{registration.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 glass-card p-4">
                <Phone className="h-5 w-5 text-[#FF6F4A] flex-shrink-0" />
                <div>
                  <p className="text-xs text-slate-400">Phone</p>
                  <p className="text-white text-sm">{registration.phone}</p>
                </div>
              </div>
              {registration.city && (
                <div className="flex items-center gap-3 glass-card p-4">
                  <MapPin className="h-5 w-5 text-[#FF6F4A] flex-shrink-0" />
                  <div>
                    <p className="text-xs text-slate-400">City</p>
                    <p className="text-white text-sm">{registration.city}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3 glass-card p-4">
                <QrCode className="h-5 w-5 text-[#FFB6C1] flex-shrink-0" />
                <div>
                  <p className="text-xs text-slate-400">Registration Type</p>
                  <p className="text-[#FF6F4A] font-semibold text-sm">{registration.registration_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 glass-card p-4">
                <IndianRupee className="h-5 w-5 text-[#FFB6C1] flex-shrink-0" />
                <div>
                  <p className="text-xs text-slate-400">Amount</p>
                  <p className="text-white font-semibold">{formatAmount(registration.amount)}</p>
                </div>
              </div>
            </div>

            {/* QR Code Download - Only for Verified */}
            {isVerified && registration.qr_code_url && (
              <div className="glass-card p-6 text-center border-green-500/20 bg-green-500/5" data-testid="qr-download-section">
                <QrCode className="h-10 w-10 text-green-400 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-white mb-2">Your Conference QR Code</h3>
                <p className="text-sm text-slate-400 mb-4">Download and save your QR code. This will be used for entry at the conference venue.</p>
                <div className="mb-4">
                  <img
                    src={`${BACKEND_URL}/api/file/${registration.qr_code_url.split('/').pop()}`}
                    alt="Conference QR Code"
                    className="w-48 h-48 mx-auto bg-white p-3 rounded-xl"
                    data-testid="qr-code-image"
                  />
                </div>
                <a
                  href={`${BACKEND_URL}/api/file/${registration.qr_code_url.split('/').pop()}`}
                  download={`AGOC2026_QR_${registration.full_name.replace(/\s+/g, '_')}.png`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
                  data-testid="download-qr-button"
                >
                  <Download className="h-4 w-4" />
                  Download QR Code
                </a>
              </div>
            )}

            {!isVerified && (
              <div className="glass-card p-4 border-yellow-500/20 bg-yellow-500/5 text-center">
                <p className="text-sm text-yellow-300">Your QR code will be available for download once your payment is verified by the AGOS committee.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyRegistrationPage;
